import { useContext, useMemo, useState } from 'react';
import { ReactiveJsonSubroot, GlobalDataContext } from '@ea-lab/reactive-json';
import { formatArchiveDate } from '../../services/archive';
import { initials } from '../shared/ui/InitialsAvatar';
import { changedTimestamp, dueDateSortKey, resolveLinkedContentTitles, resolveStatusLabel, resolveTagLabels, resolveUserName } from './kanbanRowValues';

// Defined once outside the component so the object reference is stable across renders.
// Each subroot's renderView: KanbanListRow renders the visible <tr>; DataSync watches
// ~~.apiData and POSTs on idle. DataSync renders a hidden <span>, which is INVALID as a
// direct child of <tbody> ("<span> cannot be a child of <tbody>" → hydration warning), so
// it is wrapped in a hidden <tr><td>: valid table markup, watcher stays in this subroot.
const ROW_BUILD = {
    renderView: [
        { type: 'KanbanListRow' },
        {
            type: 'tr',
            attributes: { class: 'hidden' },
            content: [
                {
                    type: 'td',
                    content: [
                        { type: 'DataSync', path: '~~.apiData', mode: 'onIdle', idleDelay: 1500 },
                    ],
                },
            ],
        },
    ],
};

// ---------------------------------------------------------------------------
// UserAvatar — still exported for KanbanBoard's assignee filter display.
// ---------------------------------------------------------------------------
export const UserAvatar = ({ name, users = [], size = 'sm' }) => {
    const sizeClasses = size === 'sm' ? 'w-5 h-5 text-[8px]' : 'w-8 h-8 text-xs';
    const user = users.find(u => String(u.id) === String(name) || u.name === name);
    // `name` may be a numeric user id (or null) when the assignee can't be
    // resolved to a loaded user. Only use it as a label when it's a real string,
    // otherwise fall back to '?', so displayName is always a string (charAt-safe).
    const displayName = user?.name || (typeof name === 'string' && name) || '?';
    return (
        <span className={`${sizeClasses} rounded-full bg-iris-100 text-iris-700 font-bold flex items-center justify-center flex-shrink-0`} title={displayName}>
            {initials(displayName)}
        </span>
    );
};

// ---------------------------------------------------------------------------
// Tri des colonnes.
//
// Les clés de tri lisent les valeurs AFFICHÉES par la ligne (libellé d'état
// résolu par le workflow, nom d'utilisateur, libellés de tags) et jamais les
// identifiants bruts : on trie sur ce qu'on voit. Les résolveurs viennent de
// kanbanRowValues pour que le tri et la recherche du board ne divergent pas.
// ---------------------------------------------------------------------------

// Une entrée par <th>, dans l'ordre de rendu : les en-têtes sont générés depuis
// cette table, donc une colonne et sa clé de tri ne peuvent pas diverger.
const SORT_COLUMNS = [
    { key: 'title', label: 'Titre', type: 'text' },
    { key: 'status', label: 'Statut', type: 'text' },
    { key: 'priority', label: 'Priorité', type: 'number' },
    { key: 'complexity', label: 'Complexité', type: 'number' },
    // 'YYYY-MM-DD' se trie déjà lexicographiquement : le type 'text' suffit, et
    // il place les lignes SANS échéance en bas dans les deux sens (comme
    // « Assigné »), ce qu'un tri numérique sur 0 ne ferait pas.
    { key: 'dueDate', label: 'Échéance', type: 'text' },
    { key: 'assignee', label: 'Assigné', type: 'text' },
    { key: 'tags', label: 'Tags', type: 'text' },
    { key: 'linkedContent', label: 'Contenu lié', type: 'text' },
    { key: 'changed', label: 'Mise à jour', type: 'date' },
];

const sortValue = (row, key, ctx) => {
    const d = row.task.data;
    switch (key) {
        case 'title': return d.title ?? '';
        case 'status': return resolveStatusLabel(row.task, ctx.columns);
        case 'priority': return d.priority ?? 0;
        case 'complexity': return d.complexity ?? 0;
        case 'dueDate': return dueDateSortKey(d.dueDate);
        case 'assignee': return resolveUserName(d.assignee, ctx.users);
        case 'tags': return resolveTagLabels(row.task, ctx.tagOptions)
            .slice().sort((a, b) => a.localeCompare(b, 'fr')).join(', ');
        case 'linkedContent': return resolveLinkedContentTitles(row.task).join(', ');
        case 'changed': return changedTimestamp(d.changed);
        default: return '';
    }
};

const compareRows = (ra, rb, sort, ctx) => {
    const column = SORT_COLUMNS.find(c => c.key === sort.key);
    const dir = sort.dir === 'desc' ? -1 : 1;
    const va = sortValue(ra, sort.key, ctx);
    const vb = sortValue(rb, sort.key, ctx);
    if (column?.type === 'number') return dir * ((Number(va) || 0) - (Number(vb) || 0));
    if (column?.type === 'date') {
        const na = Number(va) || 0;
        const nb = Number(vb) || 0;
        // Même règle que pour le texte : une ligne sans date reste en bas dans les
        // deux sens — un bloc de dates inconnues en tête n'apprend rien.
        if (!na !== !nb) return na ? -1 : 1;
        return dir * (na - nb);
    }
    const sa = String(va || '');
    const sb = String(vb || '');
    // Les cellules vides restent en bas dans LES DEUX sens : un paquet de lignes
    // non assignées en tête d'un tri « Assigné » n'apporte aucune information.
    if (!sa !== !sb) return sa ? -1 : 1;
    return dir * sa.localeCompare(sb, 'fr', { numeric: true, sensitivity: 'base' });
};

// Tri qui PRÉSERVE le groupement parent → sous-tâches : chaque bloc (un parent
// et ses propres enfants) est trié comme une unité, et les enfants sont triés à
// l'intérieur de leur bloc. Une ligne « ↳ Sous-tâche de X » ne se retrouve donc
// jamais détachée du X auquel elle fait référence.
const sortRows = (rows, sort, ctx) => {
    if (!sort.key) return rows;
    const blocks = [];
    rows.forEach(row => {
        const current = blocks[blocks.length - 1];
        const head = current?.[0];
        // Un enfant ne rejoint le bloc courant que s'il est VRAIMENT l'enfant de
        // sa tête ; une sous-tâche orpheline ouvre donc son propre bloc.
        const isChildOfHead = row.depth > 0 && head
            && String(row.task.data.parentId) === String(head.task.data.id);
        if (isChildOfHead) current.push(row); else blocks.push([row]);
    });
    blocks.forEach(block => {
        const children = block.splice(1);
        children.sort((a, b) => compareRows(a, b, sort, ctx));
        block.push(...children);
    });
    blocks.sort((a, b) => compareRows(a[0], b[0], sort, ctx));
    return blocks.flat();
};

// En-tête triable : la cellule porte un vrai <button> (opérable au clavier),
// aria-sort porte l'état pour les technologies d'assistance, le chevron pour
// l'affichage.
const SortableHeader = ({ column, sort, onSort }) => {
    const active = sort.key === column.key;
    return (
        <th
            scope="col"
            aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
            className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider"
        >
            <button
                type="button"
                onClick={() => onSort(column.key)}
                className={`group inline-flex items-center gap-1 uppercase tracking-wider ${active ? 'text-iris-700' : 'hover:text-slate-700'}`}
                title={`Trier par ${column.label}`}
            >
                {column.label}
                {active ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        {sort.dir === 'asc' ? <polyline points="18 15 12 9 6 15" /> : <polyline points="6 9 12 15 18 9" />}
                    </svg>
                ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="text-slate-300 group-hover:text-slate-400">
                        <polyline points="7 15 12 20 17 15" /><polyline points="7 9 12 4 17 9" />
                    </svg>
                )}
            </button>
        </th>
    );
};

// ---------------------------------------------------------------------------
// KanbanListView — list view for project tasks.
//
// Each task gets its own ReactiveJsonSubroot containing:
//   - DataSync (path: ~~.apiData, mode: onIdle, idleDelay: 1500)
//   - KanbanListRow plugin (the <tr> bridge component)
//
// The subroot's store is initialized with:
//   apiData  — Syncable Object { submission_url, data: { tags, priority } }
//   display  — read-only display fields (id, title, columnId, assignee…)
//   columns, users, tagOptions, typeOptions, returnTypeOptions
//
// Changes to ~~.apiData.data inside the subroot (written by KanbanListRow)
// are detected by DataSync and POSTed to submission_url after 1.5 s of idle.
// No sharedUpdates is needed — each subroot manages its own persistence cycle.
// ---------------------------------------------------------------------------
export const KanbanListView = ({
    tasks = [],
    columns = [],
    users = [],
    tagOptions = [],
    returnTypeOptions = [],
    typeOptions = [],
    // Membres du projet : seuls destinataires possibles d'une assignation.
    assignableUsers = [],
}) => {
    const gdc = useContext(GlobalDataContext);
    const realUserRole = gdc?.data?.currentUserRoleData?.role || '';
    const canManageArchive = ['Administrateur', 'Chef de projet', 'CDP', 'project-admin', 'project-cdp'].includes(realUserRole);
    // Projet archivé : tout le projet est en lecture seule, l'action d'archiver
    // une tâche disparaît (même règle que le board).
    const projectArchived = !!gdc?.data?.project?.archived;

    // Tri par défaut : priorité décroissante — les tâches les plus prioritaires
    // arrivent en tête à l'ouverture de la vue. Le cycle de clic reste inchangé,
    // donc key: null (= l'ordre du board : ordre de création, parents puis
    // enfants) reste atteignable en recliquant l'en-tête Priorité.
    const [sort, setSort] = useState({ key: 'priority', dir: 'desc' });

    const ctx = useMemo(() => ({ columns, users, tagOptions }), [columns, users, tagOptions]);
    const rows = useMemo(() => sortRows(tasks, sort, ctx), [tasks, sort, ctx]);

    // Un clic fait défiler : croissant → décroissant → retour à l'ordre du board.
    const handleSort = (key) => setSort(prev => (
        prev.key === key
            ? (prev.dir === 'asc' ? { key, dir: 'desc' } : { key: null, dir: 'asc' })
            : { key, dir: 'asc' }
    ));
    return (
        <div className="bg-white rounded-lg shadow overflow-hidden w-full">
            <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                    <tr>
                        {SORT_COLUMNS.map(column => (
                            <SortableHeader key={column.key} column={column} sort={sort} onSort={handleSort} />
                        ))}
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                    {rows.map(({ task, depth, parentTitle }) => (
                        <ReactiveJsonSubroot
                            key={task.data.id}
                            props={{
                                rjOptions: {
                                    maybeRawAppRjBuild: ROW_BUILD,
                                    dataOverride: {
                                        apiData: {
                                            submission_url: task.submission_url,
                                            data: {
                                                title: task.data.title ?? '',
                                                tags: task.data.tags ?? [],
                                                priority: task.data.priority ?? 0,
                                                complexity: task.data.complexity ?? 0,
                                                // '' → null : le champ est une référence d'entité.
                                                assignee: task.data.assignee || null,
                                            },
                                        },
                                        display: {
                                            id: task.data.id,
                                            title: task.data.title,
                                            columnId: task.data.columnId,
                                            state: task.data.state ?? '',
                            changed: task.data.changed ?? '',
                            // Échéance : la ligne la met en rouge quand elle est
                            // dépassée (cf. isOverdue, même règle que la carte).
                            dueDate: task.data.dueDate ?? '',
                                            assignee: task.data.assignee,
                                            type: task.data.type,
                                            parentId: task.data.parentId ?? 0,
                                            demande_evolution: task.data.demande_evolution,
                                            description: task.data.description || '',
                                            archived: task.data.archived || null,
                                            isArchived: !!task.data.archived,
                                            archivedLabel: task.data.archived ? `Archivé le ${formatArchiveDate(task.data.archived)}` : '',
                                            canUnarchive: canManageArchive,
                                            projectArchived,
                                            linkedContentRefs: task.data.linkedContentRefs ?? [],
                                            depth: depth ?? 0,
                                            parentTitle: parentTitle ?? '',
                                            isSubtask: (depth ?? 0) > 0,
                                        },
                                        columns,
                                        users,
                                        assignableUsers,
                                        tagOptions,
                                        typeOptions,
                                        returnTypeOptions,
                                    },
                                },
                            }}
                        />
                    ))}
                </tbody>
            </table>
        </div>
    );
};
